import { Color, Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';

import {
  ItemTypes,
  MODULE_TYPE_TO_NAME,
  ModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import {
  MODULE_ICON,
  ModuleTypeInfo,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import Accordion from 'dashboard/components/Accordion';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { BaseModule } from 'dashboard/lib/module';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Environment } from 'dashboard/models/environment';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { FeatureFlag } from 'lib/interfaces/feature-flags';

const ComponentLibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: ${Spacing[5]};
`;

const SearchSection = styled.div`
  padding: ${Spacing[5]};
  padding-top: 0;
`;

const ModuleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: ${Spacing[2]};
  grid-row-gap: ${Spacing[4]};
`;

export interface ComponentLibraryProps<
  M extends BaseModule,
  SupportedModuleTypes extends ModuleTypes,
> {
  organization: Organization;
  currentEnvironment: Environment;
  supportedModuleTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  supportedComponentTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  generateDefaultModuleConfiguration: (moduleType: SupportedModuleTypes) => M;
}

export function ComponentLibrary<M extends BaseModule, SupportedModuleTypes extends ModuleTypes>({
  organization,
  currentEnvironment,
  supportedModuleTypes,
  supportedComponentTypes,
  generateDefaultModuleConfiguration,
}: ComponentLibraryProps<M, SupportedModuleTypes>) {
  const [omni, setOmni] = useState('');
  const moduleTypesFuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 0,
      findAllMatches: true,
      keys: [{ name: 'name', weight: 1 }],
    };
    const moduleTypeInfoList = Object.values(supportedModuleTypes).map((moduleTypeInfo) => {
      return {
        name: MODULE_TYPE_TO_NAME[moduleTypeInfo.type],
        moduleTypeInfo,
      };
    });

    return new Fuse(
      moduleTypeInfoList.map((gridItemTypeInfo) => gridItemTypeInfo),
      fuseOptions,
    );
  }, []);

  const componentTypesFuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 0,
      findAllMatches: true,
      keys: [{ name: 'name', weight: 1 }],
    };

    const componentTypeInfoList = Object.values(supportedComponentTypes).map((moduleTypeInfo) => {
      return {
        name: MODULE_TYPE_TO_NAME[moduleTypeInfo.type],
        moduleTypeInfo,
      };
    });

    return new Fuse(
      componentTypeInfoList.map((gridItemTypeInfo) => gridItemTypeInfo),
      fuseOptions,
    );
  }, []);

  const filteredGridModuleTypes = useMemo(
    () =>
      omni === ''
        ? Object.values(supportedModuleTypes)
        : moduleTypesFuse.search(omni).map(({ item }) => item.moduleTypeInfo),
    [omni, moduleTypesFuse],
  );
  const filteredComponentTypes = useMemo(
    () =>
      omni === ''
        ? Object.values(supportedComponentTypes)
        : componentTypesFuse.search(omni).map(({ item }) => item.moduleTypeInfo),
    [omni, componentTypesFuse],
  );

  return (
    <ComponentLibraryContainer>
      <SearchSection>
        <SearchInput
          id='component-library__search'
          title={null}
          handleInput={setOmni}
          value={omni}
          placeholder='Search modules'
          size='md'
        />
      </SearchSection>
      <ControlPanelDivider full />
      <Section data-test='component-library__prebuilt-modules'>
        {filteredGridModuleTypes.length > 0 && (
          <Accordion title='Prebuilt modules' defaultExpanded={true}>
            <Spacer height={3} />
            <ModuleContainer>
              {filteredGridModuleTypes.map(
                (moduleTypeInfo: ModuleTypeInfo<SupportedModuleTypes>) => {
                  const {
                    type: moduleType,
                    productFeatures,
                    featureFlag,
                    internalUsersOnly,
                  } = moduleTypeInfo;
                  return (
                    <ModuleDisplay
                      key={moduleType}
                      idPrefix={`component-library__prebuilt-modules--${moduleType}`}
                      icon={MODULE_ICON[moduleType]}
                      label={MODULE_TYPE_TO_NAME[moduleType]}
                      featureFlag={featureFlag}
                      productFeatures={productFeatures}
                      internalUsersOnly={internalUsersOnly}
                      organization={organization}
                      currentEnvironment={currentEnvironment}
                      generateDefaultModule={() => generateDefaultModuleConfiguration(moduleType)}
                    />
                  );
                },
              )}
            </ModuleContainer>
          </Accordion>
        )}
        <Spacer height={3} />
        {filteredComponentTypes.length > 0 && (
          <Accordion title='Components' defaultExpanded={true}>
            <Spacer height={3} />
            <ModuleContainer>
              {filteredComponentTypes.map(
                (moduleTypeInfo: ModuleTypeInfo<SupportedModuleTypes>) => {
                  const {
                    type: moduleType,
                    productFeatures,
                    featureFlag,
                    internalUsersOnly,
                  } = moduleTypeInfo;
                  return (
                    <ModuleDisplay
                      key={moduleType}
                      idPrefix={`component-library__components--${moduleType}`}
                      icon={MODULE_ICON[moduleType]}
                      label={MODULE_TYPE_TO_NAME[moduleType]}
                      featureFlag={featureFlag}
                      productFeatures={productFeatures}
                      internalUsersOnly={internalUsersOnly}
                      organization={organization}
                      currentEnvironment={currentEnvironment}
                      generateDefaultModule={() => generateDefaultModuleConfiguration(moduleType)}
                    />
                  );
                },
              )}
            </ModuleContainer>
          </Accordion>
        )}
      </Section>
    </ComponentLibraryContainer>
  );
}

const ModuleDisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconContainer = styled.div`
  ${hoverableMixin}
  background-color: ${Color.CoreSlate50};
  width: 112px;
  height: 112px;
  position: relative;

  & svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 75%;
    height: 100%;
  }
`;

interface ModuleDisplayProps<M extends BaseModule> {
  idPrefix: string;
  icon: ReactNode;
  label: string;
  featureFlag: FeatureFlag | null;
  productFeatures: ProductFeatureName[] | null;
  internalUsersOnly: boolean;
  organization: Organization;
  currentEnvironment: Environment;
  generateDefaultModule: () => M;
}

function ModuleDisplay<M extends BaseModule>({
  idPrefix,
  icon,
  label,
  featureFlag,
  productFeatures,
  internalUsersOnly,
  organization,
  currentEnvironment,
  generateDefaultModule,
}: ModuleDisplayProps<M>) {
  const [, dragRef, preview] = useDrag(
    () => ({
      type: ItemTypes.LibraryComponent,
      item: { module: generateDefaultModule() },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [],
  );

  // disable default drag preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const featureFlagEnabled = useFeatureFlag(featureFlag);
  const isPersonerd = useIsPersonerd();
  const anyProductFeatureEnabled =
    productFeatures &&
    productFeatures.some(
      (productFeature) =>
        !productFeatureDisabled(organization, productFeature, currentEnvironment.type),
    );

  const hideModule =
    (featureFlag && !featureFlagEnabled) ||
    (internalUsersOnly && !isPersonerd) ||
    (productFeatures && !anyProductFeatureEnabled);

  if (hideModule) {
    return null;
  }

  return (
    <ModuleDisplayContainer>
      <IconContainer id={idPrefix} ref={dragRef} data-test={idPrefix}>
        {icon}
        {internalUsersOnly && isPersonerd && (
          <Sticker
            css={`
              position: absolute;
              bottom: 0;
              right: 0;
              margin-bottom: ${Spacing[2]};
              margin-right: ${Spacing[2]};
            `}
            variant='purple'
          >
            Internal
          </Sticker>
        )}
      </IconContainer>
      <Spacer height={1} />
      <Paragraph id={`${idPrefix}-label`} size='sm' color='primary'>
        {label}
      </Paragraph>
    </ModuleDisplayContainer>
  );
}

export default ComponentLibrary;
